import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";


const caseStudyDetails = {
  "global-wealth": {
    category: "Financial Firm",
    kicker: "Leading Global Wealth Manager",
    headline: "Global Wealth Management firm builds a BeSpoke AI Dashboard to automate it workflows end-to-end",
    subheadline:
      "We support a global wealth manager operating across seven offices worldwide.",
    heroImage: "/assets/map2.png",

    // About the client (top)
    intro: [
      "Our client is a rapidly growing global wealth management firm specialising in serving ultra-high-net-worth individuals (UHNWIs), entrepreneurs, and single-family offices.",
      "As their operations expanded, they required a scalable, secure, and integrated digital infrastructure to streamline services across multiple offices worldwide—without compromising governance or client experience.",
    ],

    // Challenge + Solution
    sections: [
      {
        label: "The challenge",
        accent: "#AF4AFA",
        title: "Fragmented workflows and rising compliance overhead",
        body: [
          "As teams scaled globally, workflows became spread across disconnected tools and manual processes—creating duplicated effort, inconsistent data, and operational drag.",
          "Onboarding and case execution slowed as volume increased, while cross-jurisdiction operations required stronger standardisation, traceability, and audit readiness.",
        ],
        image: "/assets/fragmented.png",
      },
      {
        label: "The solution",
        title: "A secure, unified digital ecosystem",
        accent: "#AF4AFA",
        body: [
          "We developed a custom digital ecosystem tailored to their needs: a bespoke modular dashboard, private cloud infrastructure, and a secure iOS application.",
          "We also centralised their technology stack, built custom automation features for existing workflows, and deployed a private LLM capability within the client boundary.",
        ],
        image:
          "https://images.unsplash.com/photo-1556155092-490a1ba16284?auto=format&fit=crop&w=1800&q=80",
      },
    ],

    whatWeBuiltTitle: "What we built",
    whatWeBuilt: [
      {
        title: "Bespoke modular dashboard",
        body:
          "Role-based modules that centralise client context, approvals, routing, and reporting—designed to evolve as the firm scales.",
      },
      {
        title: "iOS mobile application",
        body:
          "Secure advisor companion app to surface key client context, tasks, and updates with controlled access.",
      },
      {
        title: "Centralised tech stack",
        body:
          "Consolidated fragmented tooling into one operating layer with consistent data models and shared governance controls.",
      },
      {
        title: "Workflow automation layer",
        body:
          "Custom features that automate existing processes end-to-end—routing, approvals, document generation, and status updates—aligned to internal controls.",
      },
      {
        title: "Private cloud deployment",
        body:
          "Private cloud environment with audit logging, access controls, encryption, and operational monitoring for compliance-ready operations.",
      },
      {
        title: "Private LLM within client boundary",
        body:
          "Private AI capability to accelerate knowledge retrieval and drafting—keeping sensitive data within the firm’s environment and controls.",
      },
    ],

    // Impact (no testimonial)
    impact: {
      label: "The Impact",
      accent: "#AF4AFA",
      title: "Measurable improvements across operations, governance, and scale",
      blocks: [
        {
          title: "Streamlined Operations & Efficiency",
          body:
            "By automating key workflows, integrating third-party services, and centralising data, we cut manual effort by 75%, reduced data-entry errors by 60%, and accelerated case turnaround by 28%.",
        },
        {
          title: "Enhanced Security & Compliance",
          body:
            "Our private cloud infrastructure adheres to leading regulatory and cybersecurity standards, ensuring secure data handling, reducing compliance workload by 65%, and enabling seamless cross-jurisdiction operations.",
        },
        {
          title: "Data-Driven Growth & Scalability",
          body:
            "The custom-built digital infrastructure provided a strong foundation for expansion, enabling 60% faster onboarding and efficient management of a growing client base—while maintaining operational agility and service excellence.",
        },
      ],
    },
  },

  "wealth-dynamix": {
    category: "Financial Firm",
    kicker: "Wealth Dynamix",
    headline: "AI-enhanced client experiences",
    subheadline: "Custom AI modules integrated into an existing CLM platform—built for security and scale.",
    heroImage:
      "https://images.pexels.com/photos/1827882/pexels-photo-1827882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    intro: [
      "Wealth Dynamix needed to enhance their client lifecycle management platform with AI capabilities while maintaining strict data security and compliance standards for their wealth management clients.",
    ],
    sections: [
      {
        label: "The solution",
        title: "AI modules embedded into existing workflows",
        body: [
          "We developed custom AI modules that integrate with Wealth Dynamix’s CLM platform, enabling intelligent client insights, workflow recommendations, and predictive analytics.",
        ],
        image:
          "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&w=1800&q=80",
      },
    ],
    whatWeBuiltTitle: "What we built",
    whatWeBuilt: [
      {
        title: "AI insights layer",
        body: "Client insights and recommendations surfaced within the workflow.",
      },
      {
        title: "Predictive analytics",
        body: "Signals to support prioritisation and proactive outreach.",
      },
    ],
    impact: {
      label: "The Impact",
      accent: "#AF4AFA",
      title: "Faster, more personalised advisor workflows",
      blocks: [
        { title: "30% higher engagement", body: "Improved advisor–client interaction quality and consistency." },
        { title: "80% routine comms automated", body: "Templates and triggers reduced repetitive work." },
        { title: "Real-time insights", body: "Portfolio and client signals available in-context." },
      ],
    },
  },
};

function Pill({ children }) {
  return (
    <span
      className="inline-flex items-center text-xs px-3 py-1 rounded-full"
      style={{
        background: "rgba(255,255,255,0.10)",
        border: "1px solid rgba(255,255,255,0.10)",
        color: "#F5F5F5",
        backdropFilter: "blur(8px)",
      }}
    >
      {children}
    </span>
  );
}

function StorySection({ section, flip = false }) {
  const hasImage = Boolean(section.image);

  // Bigger-than-subheading text enforced via inline fontSize (so it definitely changes)
  const bodyStyle = { color: "#A1A1AA", fontSize: "18px", lineHeight: "1.7" }; // ~text-lg
  const bodyStyleMd = { color: "#A1A1AA", fontSize: "19px", lineHeight: "1.75" };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
      {/* Text */}
      <div className={flip ? "lg:order-2" : ""}>
        <p
  className="uppercase mb-4"
  style={{
    color: section.accent ?? "#AF4AFA",
    fontSize: "14px",
    letterSpacing: "0.14em",
  }}
>
  {section.label}
</p>

        <h2
  className="font-medium mb-4"
  style={{
    fontFamily: "'Lora', serif",
    color: "#F5F5F5",
    fontSize: "clamp(28px, 2.6vw, 44px)",
    lineHeight: 1.08,
  }}
>
  {section.title}
</h2>

        <div className="space-y-4">
          {section.body?.map((p, idx) => (
            <p
              key={idx}
              style={window.innerWidth >= 768 ? bodyStyleMd : bodyStyle}
            >
              {p}
            </p>
          ))}
        </div>
      </div>

      {/* Image */}
      {hasImage && (
        <div className={flip ? "lg:order-1" : ""}>
          <div className="overflow-hidden rounded-2xl">
            <img
              src={section.image}
              alt={section.title}
              className="w-full h-full object-cover"
              style={{ aspectRatio: "16 / 10" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function CaseStudyDetailPage() {
  const { id } = useParams();
  const study = caseStudyDetails[id];

  if (!study) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="text-center">
          <h1 className="text-2xl mb-4" style={{ color: "#F5F5F5" }}>
            Case study not found
          </h1>
          <Link to="/case-studies" className="text-[#7A85E2] hover:underline">
            Back to Case Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#050505" }}>
      {/* Back */}
      <div className="pt-28 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm" style={{ color: "#A1A1AA" }}>
            <ArrowLeft size={16} />
            Back to Case Studies
          </Link>
        </div>
      </div>


      {/* HERO */}
      <section className="pt-8 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <Pill>{study.category}</Pill>
            {study.kicker && <span className="text-xs" style={{ color: "#A1A1AA" }}>{study.kicker}</span>}
          </div>

          {study.headline && (
            <h1
              className="font-medium leading-tight mb-4"
              style={{ fontFamily: "'Lora', serif", color: "#F5F5F5", fontSize: "clamp(42px, 4vw, 64px)" }}
            >
              {study.headline}
            </h1>
          )}

          {study.subheadline && (
            <p className="max-w-4xl mb-8" style={{ color: "#A1A1AA", fontSize: "18px", lineHeight: 1.7 }}>
              {study.subheadline}
            </p>
          )}

          {study.heroImage && (
            <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="relative" style={{ aspectRatio: "16 / 7" }}>
                <img src={study.heroImage} alt={study.headline} className="w-full h-full object-cover" />
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.10) 0%, rgba(5,5,5,0.55) 100%)" }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ABOUT THE CLIENT (top) */}
      {study.intro && (
        <section className="pb-12 px-6 md:px-12 lg:px-24">
          <div className="max-w-6xl mx-auto">
            <h2
              className="font-medium mb-4"
              style={{ fontFamily: "'Lora', serif", color: "#F5F5F5", fontSize: "clamp(28px, 2.2vw, 40px)" }}
            >
              {study.kicker || "About the client"}
            </h2>
            <div className="space-y-4">
              {study.intro.map((p, idx) => (
                <p key={idx} style={{ color: "#A1A1AA", fontSize: "18px", lineHeight: 1.75 }}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CHALLENGE + SOLUTION (MORE SPACE BETWEEN SECTIONS) */}
      <section className="pb-14 px-6 md:px-12 lg:px-24">
        <div
          className="max-w-6xl mx-auto"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "96px", // <-- guaranteed spacing (won't depend on Tailwind)
          }}
        >
          {study.sections?.map((s, idx) => (
            <StorySection key={idx} section={s} flip={idx % 2 === 1} />
          ))}
        </div>
      </section>

{/* WHAT WE BUILT (ABOVE IMPACT) */}
{study.whatWeBuilt && (
  <section className="pb-16 px-6 md:px-12 lg:px-24">
    <div className="max-w-6xl mx-auto">
      <h2
        className="font-medium mb-6"
        style={{
          fontFamily: "'Lora', serif",
          color: "#F5F5F5",
          fontSize: "clamp(28px, 2.2vw, 40px)",
        }}
      >
        {study.whatWeBuiltTitle || "What we built"}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {study.whatWeBuilt.map((b) => (
          <div
            key={b.title}
            className="rounded-2xl p-6"
            style={{
              background: "linear-gradient(180deg, #0B0B10 0%, #1E1F23 100%)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
            }}
          >
            <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}>
              {b.title}
            </h3>
            <p style={{ color: "rgba(245,245,245,0.75)", fontSize: "16px", lineHeight: 1.65 }}>
              {b.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
)}

{/* IMPACT (LABEL OUTSIDE TILE) */}
{study.impact && (
  <section className="pb-16 px-6 md:px-12 lg:px-24">
    <div className="max-w-6xl mx-auto">
      <p
  className="uppercase mb-4"
  style={{
    color: study.impact.accent ?? "#AF4AFA",
    fontSize: "14px",
    letterSpacing: "0.14em",
  }}
>
  {study.impact.label}
</p>

      <div
        className="rounded-2xl p-6 md:p-8"
        style={{
          background:
            "radial-gradient(900px 420px at 18% 18%, rgba(99,115,250,0.18) 0%, rgba(175,74,250,0.10) 38%, rgba(10,10,15,0.92) 72%), linear-gradient(180deg, rgba(17,18,27,0.92) 0%, rgba(10,10,15,0.92) 100%)",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
        }}
      >
        <h2
          className="font-medium mb-6"
          style={{ fontFamily: "'Lora', serif", color: "#F5F5F5", fontSize: "clamp(22px, 1.8vw, 30px)" }}
        >
          {study.impact.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {study.impact.blocks.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl p-6"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <h3 className="text-lg md:text-xl font-medium mb-3" style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}>
                {b.title}
              </h3>
              <p style={{ color: "#A1A1AA", fontSize: "16px", lineHeight: 1.7 }}>{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
)}

<Footer />
</div>
);
}
